import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useIntl } from 'react-intl';

import StandardTextEditor, { TranslatedDraft } from '../../../common/editor/standardTextEditor';
import CollapseButton from '../buttons/collapseButton';

interface DescriptionDialogProps {
  open: boolean;
  text: string;
  title?: string;
  onChangeText: (content: string) => void;
  setClose: () => void;
  hideTitle?: boolean;
  variant?: string;
  messageButton?: JSX.Element;
  attachedItems?: JSX.Element,
  isEditable?: boolean;
  enableTranslate?: boolean;
  closeButton?: JSX.Element;
  setTranslatedDraftObject?: (originalMessage: string, translatedObject: TranslatedDraft | null) => void,
  translatedDraftMessageVariables?: TranslateDraftMessageVariables;
}

interface TranslateDraftMessageVariables {
  loadingDraftTranslation: boolean;
  setLoadingDraftTranslation: React.Dispatch<React.SetStateAction<boolean>>;
  draftLanguage: string;
  setDraftLanguage: React.Dispatch<React.SetStateAction<string>>;
  switchDraftMessage: boolean;
  setSwitchDraftMessage: React.Dispatch<React.SetStateAction<boolean>>;
  originalDraftMessage: string;
  setOriginalDraftMessage: React.Dispatch<React.SetStateAction<string>>;
  translatedDraftMessage: string;
  setTranslatedDraftMessage: React.Dispatch<React.SetStateAction<string>>;
}

const DescriptionDialog = ({
  open,
  text,
  onChangeText,
  setClose,
  variant = 'default',
  hideTitle,
  messageButton,
  attachedItems,
  closeButton,
  title = 'Description',
  isEditable = true,
  enableTranslate,
  setTranslatedDraftObject,
  translatedDraftMessageVariables,
}: DescriptionDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();

  const getDialogTitle = () => {
    if (title === 'Description') {
      return intl.formatMessage({ defaultMessage: 'Description ' });
    }
    return title;
  };

  return (
    <Dialog
      open={open}
      onClose={setClose}
      fullWidth
      maxWidth='xl'
      PaperProps={{
        sx: {
          height: { sm: '80vh' },
        },
      }}
      fullScreen={isMobile}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        padding='12px 10px 12px 20px'
        sx={{
          backgroundColor: () => (variant === 'message' ? '#F6F6F6' : 'none'),
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '17px',
            letterSpacing: '0em',
            textAlign: 'left',
            mt: '1rem',
            mb: '0.5rem',
          }}
        >
          {!hideTitle && getDialogTitle()}
        </Typography>
        <Stack direction='row' alignItems='center' spacing={0}>
          <CollapseButton
            handleButtonClicked={setClose}
            variant={variant === 'message' ? 'dark' : 'standard'}
          />
          {closeButton}
        </Stack>
      </Stack>
      <DialogContent
        sx={{
          padding: 0,
        }}
      >
        <StandardTextEditor
          text={text}
          expand={open}
          onChangeText={onChangeText}
          variant={variant}
          messageButton={messageButton}
          attachedItems={attachedItems}
          customPlaceholder={
            variant === 'message'
              ? intl.formatMessage({ defaultMessage: 'Type here...' })
              : undefined
          }
          enableTranslate={enableTranslate}
          isEditable={isEditable}
          setTranslatedDraftObject={setTranslatedDraftObject}
          translatedDraftMessageVariables={translatedDraftMessageVariables}
        />
      </DialogContent>
    </Dialog>
  );
};

export default DescriptionDialog;
